import React, { useEffect, useRef, useState } from "react";

// @mui material components
import Grid from "@mui/material/Grid";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";

// Material Dashboard 2 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";

// local
import Filters from "./Filters";
import Profile from "./Profile";
import Reports from "./Reports";
import DonutChart from "./DonutChart";
import SubCategories from "./SubCategories";
import LineChart from "./LineChart";
import Tips from "./Tips";
import CognitiveFitnessProgress from "./CognitiveFitnessProgress";

// redux
import { useDispatch, useSelector } from "react-redux";
import * as actions from "redux/actions/dashboard";
import * as actionTypes from "redux/constants";
import * as utils from "redux/utils";
import { getChartInsight } from "redux/data";
import FormField from "../account/components/FormField";
import InputFilters from "./FiltersInput";
import moment from "moment";
import { clientRequest } from "redux/requests";
import * as apis from "redux/requests/apis";

const MEMORY = "Memory";

function Dashboard() {
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);
  const dashboard = useSelector((state) => state.dashboard);

  const [user, setUser] = useState({ id: 1, name: "admin@cosmaonline.com" });
  const [report, setReport] = useState({ id: 1, name: "Last Week" });
  const [startDate, setStartDate] = useState(utils.parseDate(new Date()));
  const [endDate, setEndDate] = useState(utils.parseDate(new Date()));
  const [cat, setCat] = useState("");
  const [subCat, setSubCat] = useState("");
  const chartSectionRef = useRef(null);
  const [profile, setProfile] = useState({ id: 1, name: "Admin", email: "admin@cosmaonline.com" });
  const [memory, setMemory] = useState({ name: "memory", value: "Long Term" });
  const [userID, setuserID] = useState(undefined);
  const [reportTimeLine, setreportTimeLine] = useState({
    fromDate: moment().format("yyyy-MM-DD"),
    toDate: moment().format("yyyy-MM-DD"),
  });
  const [fitnessTimeSeries, setFitnessTimeSeries] = useState({ labels: [], scores: [] });
  const [fitnessLoading, setFitnessLoading] = useState(false);

  // Build 4 evenly-spaced segments from a date range.
  // For single-day ("today") always extend back 3 extra days so we get 4 points.
  const buildSegments = (fromDate, toDate) => {
    const start = moment(fromDate);
    const end = moment(toDate);
    const totalDays = end.diff(start, "days");
    const effectiveStart = totalDays < 3 ? end.clone().subtract(3, "days") : start;
    const effectiveDays = end.diff(effectiveStart, "days");
    const segmentSize = Math.max(1, Math.floor(effectiveDays / 4));
    const segments = [];
    for (let i = 0; i < 4; i++) {
      const segFrom = effectiveStart.clone().add(i * segmentSize, "days");
      const segTo = i === 3 ? end.clone() : effectiveStart.clone().add((i + 1) * segmentSize - 1, "days");
      segments.push({
        fromDate: segFrom.format("YYYY-MM-DD"),
        toDate: segTo.format("YYYY-MM-DD"),
        label: segTo.format("DD MMM"),
      });
    }
    return segments;
  };

  const fetchFitnessTimeSeries = async (uid, fromDate, toDate) => {
    if (!uid) return;
    setFitnessLoading(true);
    try {
      const segments = buildSegments(fromDate, toDate);
      const results = await Promise.all(
        segments.map((seg) =>
          clientRequest(
            "GET",
            `${apis.GET_COGNITIVE_FITNESS_API}?userId=${uid}&fromDate=${seg.fromDate}&toDate=${seg.toDate}`
          ).then((res) => ({ label: seg.label, score: res.data?.fitnessScore ?? 0 }))
          .catch(() => ({ label: seg.label, score: 0 }))
        )
      );
      setFitnessTimeSeries({
        labels: results.map((r) => r.label),
        scores: results.map((r) => r.score),
      });
    } finally {
      setFitnessLoading(false);
    }
  };

  // period string â†’ Summary API period param
  const toPeriodParam = (reportName) => {
    const n = (reportName || "").toLowerCase().trim();
    if (n.includes("year")) return "yearly";
    if (n.includes("month")) return "monthly";
    if (n.includes("today") || n.includes("day")) return "daily";
    return "weekly";
  };

  // Load all dashboard data on mount for EndUser
  useEffect(() => {
    const userId = auth?.user?.userId || auth?.user?.id;
    const defaultDates = {
      fromDate: moment().subtract(7, "days").format("yyyy-MM-DD"),
      toDate: moment().format("yyyy-MM-DD"),
    };
    if ((auth?.user?.role === "user" || auth?.user?.role === "patient") && auth?.user?.email) {
      if (userId) {
        const initDates = getDates("last week");
        dispatch(actions.getActivitySummary(userId, "weekly", initDates.fromDate, initDates.toDate));  // summary: scores, behavioural, dashboard+Trend
        dispatch(actions.getSessionSummary(userId));             // Tips: streak, overallInsight, categoryDeltas
        dispatch(actions.getPlayReportById(userId, defaultDates)); // Tips: play %
        fetchFitnessTimeSeries(userId, defaultDates.fromDate, defaultDates.toDate); // CognitiveFitnessProgress
      }
    }
  }, []);

  const handleCat = (value) => {
    setCat(value);
    setSubCat("");
    setTimeout(() => {
      chartSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  const handleSubCat = (value) => {
    setSubCat(value);
    // When a Long/Short Term slice is clicked in the Memory donut, sync the radio
    if (value?.name === "Long Term" || value?.name === "Short Term") {
      setMemory((m) => ({ ...m, value: value.name }));
    }
  };

  const handleUser = (name, value) => {
    setUser(value);
    dispatch(actions.getReports(profile?.id));
    setProfile(profile);
  };

  const getDates = (name) => {
    switch (name) {
      case "last year":
        return { fromDate: moment().subtract(365, "days").format("yyyy-MM-DD"), toDate: moment().format("yyyy-MM-DD") };
      case "last month":
        return { fromDate: moment().subtract(30, "days").format("yyyy-MM-DD"), toDate: moment().format("yyyy-MM-DD") };
      case "last week":
        return { fromDate: moment().subtract(7, "days").format("yyyy-MM-DD"), toDate: moment().format("yyyy-MM-DD") };
      case "today":
        return { fromDate: moment().format("yyyy-MM-DD"), toDate: moment().format("yyyy-MM-DD") };
      default:
        return null;
    }
  };

  const handleReport = (name, value) => {
    setReport(value);
    setCat("");
    setSubCat("");
    const dates = getDates(value.name.toLowerCase().trim());
    if (!dates) return;
    setreportTimeLine(dates);
    const userId = auth?.user?.userId || auth?.user?.id;
    const uid = (auth.user?.role === "user" || auth.user?.role === "patient") ? userId : userID;
    if (uid) {
      dispatch(actions.getActivitySummary(uid, toPeriodParam(value.name), dates.fromDate, dates.toDate));
      dispatch(actions.getSessionSummary(uid, dates));
      dispatch(actions.getPlayReportById(uid, dates));
      fetchFitnessTimeSeries(uid, dates.fromDate, dates.toDate);
    }
  };

  const handleSubmitReport = () => {
    setCat("");
    setSubCat("");
    const userId = auth?.user?.userId || auth?.user?.id;
    const uid = (auth.user?.role === "user" || auth.user?.role === "patient") ? userId : userID;
    if (uid) {
      dispatch(actions.getActivitySummary(uid, toPeriodParam("custom"), reportTimeLine.fromDate, reportTimeLine.toDate));
      dispatch(actions.getSessionSummary(uid, reportTimeLine));
      dispatch(actions.getPlayReportById(uid, reportTimeLine));
      fetchFitnessTimeSeries(uid, reportTimeLine.fromDate, reportTimeLine.toDate);
    }
  };

  const handleChange = (e) => {
    const clone = { ...reportTimeLine };
    if (e.target.name === "startDate") {
      setStartDate(e.target.value);

      clone.fromDate = e.target.value;
      setreportTimeLine(clone);
    } else {
      clone.toDate = e.target.value;
      setEndDate(e.target.value);
      setreportTimeLine(clone);
      // dispatch(actions.getReports(userID, clone));
    }
  };

  const handleMemoryType = (e) => {
    const val = e.target.value;
    setMemory({ ...memory, value: val });
    // Reset subCat to the top-level Long/Short Term object so Row 2 types refresh
    const topLevel = val === "Long Term" ? dashboard.memories.longTerm : dashboard.memories.shortTerm;
    setSubCat(topLevel || "");
  };

  const handleUserIDChange = (e) => {
    setuserID(e.target.value);
  };

  const memoryColor = (color) => {
    switch (color) {
      case "episodic memory":
        return "primary";
      case "procedural memory":
        return "secondary";
      case "semantic memory":
        return "warning";
      case "working memory":
        return "primary";
      case "working spatial memory":
        return "secondary";
      default:
        return "info";
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <>
        {/* .    if (auth.user.role === "user") { */}
        {actionTypes.ADVANCED_SECTION.includes(auth.user?.role) && (
          <Grid container>
            <Grid item xs={12} md={12}>
              <MDBox mt={3}>
                <InputFilters handleUserIDChange={handleUserIDChange} />
              </MDBox>
            </Grid>
            {/* <Grid item xs={12} md={12}>
              <MDBox mt={4}>
                <Reports
                  dashboard={dashboard}
                  report={report}
                  handleReport={handleReport}
                  startDate={startDate}
                  endDate={endDate}
                  handleChange={handleChange}
                />
              </MDBox>
            </Grid> */}
          </Grid>
        )}
        <Grid container>
          {/* <Grid item xs={12} md={6}>
              <MDBox mt={4} pr={2}>
                <Profile id={profile.id} name={profile.name} email={profile.email} />
              </MDBox>
            </Grid> */}

          <Grid item xs={12} md={12}>
            <MDBox mt={4}>
              <Reports
                dashboard={dashboard}
                report={report}
                handleReport={handleReport}
                startDate={startDate}
                endDate={endDate}
                handleChange={handleChange}
                handleSubmitReport={handleSubmitReport}
              />
            </MDBox>
          </Grid>
        </Grid>
      </>

      {/* 1 â”€â”€ COGNITIVE FITNESS SCORE â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      <Grid container mt={2}>
        <Grid item xs={12}>
          <CognitiveFitnessProgress
            labels={fitnessTimeSeries.labels}
            scores={fitnessTimeSeries.scores}
            loading={fitnessLoading}
            report={report}
          />
        </Grid>
      </Grid>

      {/* 2 â”€â”€ COGNITIVE PROGRESS SUMMARY â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      <Grid container mt={3}>
        <Grid item xs={12}>
          <Tips
            sessionSummary={dashboard.sessionSummary}
            loading={dashboard.sessionSummaryLoading}
            playPct={dashboard.playReportById?.playReport?.periodUserActivity ?? null}
            categoryScores={dashboard.activitySummary?.categories}
          />
        </Grid>
      </Grid>

      {/* 3 â”€â”€ SCORE BOARD â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      <Grid container>
        <Grid item xs={12} md={12}>
          <MDBox py={3} pr={1} mt={1.5}>
            <Grid container spacing={3}>
              {(() => {
                const staticCats = dashboard?.categories || [];
                // Prefer pre-aggregated summary scores; fall back to live dashboardData
                const summaryScores = dashboard?.activitySummary?.categories || [];
                const apiScores =
                  Array.isArray(dashboard?.dashboardData) && dashboard.dashboardData.length > 0
                    ? dashboard.dashboardData[0]?.DashboardScores || []
                    : [];
                const merged = staticCats.map((sc) => {
                  const fromSummary = summaryScores.find(
                    (s) => (s.category || s.Category || "").toLowerCase() === sc.name?.toLowerCase()
                  );
                  const api = apiScores.find(
                    (a) => (a.name || a.Name || "").toLowerCase() === sc.name?.toLowerCase()
                  );
                  // Always spread api so subCategoryList/Chart/SubCategoryList data is preserved.
                  // fromSummary score (pre-aggregated period score) overrides the raw api score.
                  if (fromSummary) {
                    const summaryScore = fromSummary.score ?? fromSummary.Score;
                    return { ...sc, ...(api || {}), score: summaryScore };
                  }
                  return api ? { ...sc, ...api, score: api.score ?? api.Score ?? 0 } : { ...sc, score: 0 };
                });
                return merged.map((ele, idx) => (
                  <Grid key={idx} item xs={12} md={4}>
                    <MDBox mb={1.5} style={{ cursor: "pointer" }} onClick={() => handleCat(ele)}>
                      <ComplexStatisticsCard
                        color={ele.color}
                        icon={ele.icon}
                        title={ele.name}
                        count={ele.score != null ? ele.score.toFixed(2) : ele.count ?? 0}
                        percentage={{
                          color: "success",
                          amount: "",
                          label: `${report?.name}`,
                        }}
                        cat={cat}
                        catData={ele}
                      />
                    </MDBox>
                  </Grid>
                ));
              })()}
            </Grid>
          </MDBox>
        </Grid>
      </Grid>

      {cat && (
        <div ref={chartSectionRef}>
          {/* â”€â”€ MEMORY â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
          {cat?.name?.toLowerCase() === "memory" && (
            <>
              {/* Row 1: Donut (Long Term / Short Term) â€” click slice to select; right shows that subcat chart */}
              <Grid container spacing={3} mt={1}>
                <Grid item xs={12} md={4}>
                  <DonutChart
                    title="Memory Overview"
                    chart={[
                      {
                        name: "Long Term",
                        score: dashboard.memories.longTerm?.Score ?? dashboard.memories.longTerm?.score ?? 0,
                        Chart: dashboard.memories.longTerm?.Chart || [],
                        SubSubCategoryList: dashboard.memories.longTerm?.SubSubCategoryList || dashboard.memories.longTerm?.subSubCategoryList || [],
                      },
                      {
                        name: "Short Term",
                        score: dashboard.memories.shortTerm?.Score ?? dashboard.memories.shortTerm?.score ?? 0,
                        Chart: dashboard.memories.shortTerm?.Chart || [],
                        SubSubCategoryList: dashboard.memories.shortTerm?.SubSubCategoryList || dashboard.memories.shortTerm?.subSubCategoryList || [],
                      },
                    ]}
                    handleSubCat={handleSubCat}
                    report={report}
                  />
                </Grid>
                <Grid item xs={12} md={8}>
                  <LineChart
                    title={memory.value === "Long Term" ? "Long Term Memory" : "Short Term Memory"}
                    color={memory.value === "Long Term" ? "info" : "primary"}
                    chart={
                      memory.value === "Long Term"
                        ? dashboard.memories.longTerm?.Chart || []
                        : dashboard.memories.shortTerm?.Chart || []
                    }
                    trend={
                      memory.value === "Long Term"
                        ? (dashboard.memories.longTerm?.Trend || dashboard.memories.longTerm?.trend)
                        : (dashboard.memories.shortTerm?.Trend || dashboard.memories.shortTerm?.trend)
                    }
                    insight={getChartInsight(memory.value)}
                    report={report}
                  />
                </Grid>
              </Grid>

              {/* Row 2: sub-sub categories of the selected Long/Short term */}
              <Grid container spacing={3} mt={1}>
                <Grid item xs={12} md={4}>
                  <SubCategories
                    types={(() => {
                      const defaultLongTerm = [
                        { name: "Episodic Memory",   score: 0 },
                        { name: "Procedural Memory", score: 0 },
                        { name: "Semantic Memory",   score: 0 },
                      ];
                      const defaultShortTerm = [
                        { name: "Spatial Memory",  score: 0 },
                        { name: "Working Memory",  score: 0 },
                      ];
                      const normalize = (arr) =>
                        (arr || []).map((item) => ({
                          ...item,
                          name:  item.name  || item.Name  || "",
                          score: item.score ?? item.Score ?? 0,
                        }));
                      const fromSubCat = subCat?.SubSubCategoryList || subCat?.subSubCategoryList;
                      if (fromSubCat?.length > 0) return normalize(fromSubCat);
                      const fromMemory = memory.value === "Long Term"
                        ? dashboard.memories.longTerm?.SubSubCategoryList ||
                          dashboard.memories.longTerm?.subSubCategoryList
                        : dashboard.memories.shortTerm?.SubSubCategoryList ||
                          dashboard.memories.shortTerm?.subSubCategoryList;
                      if (fromMemory?.length > 0) return normalize(fromMemory);
                      return memory.value === "Long Term" ? defaultLongTerm : defaultShortTerm;
                    })()}
                    subCat={subCat}
                    handleSubCat={handleSubCat}
                    memory={memory}
                    handleMemoryType={handleMemoryType}
                  />
                </Grid>
                <Grid item xs={12} md={8}>
                  <LineChart
                    title={subCat?.name || (memory.value === "Long Term" ? "Long Term Memory" : "Short Term Memory")}
                    color={memoryColor(subCat?.name?.toLowerCase().trim())}
                    chart={(() => {
                      const subCatSSL = subCat?.SubSubCategoryList || subCat?.subSubCategoryList;
                      if (subCat && !(subCatSSL?.length > 0)) return subCat.Chart || [];
                      return (memory.value === "Long Term"
                        ? dashboard.memories.longTerm?.Chart
                        : dashboard.memories.shortTerm?.Chart) || [];
                    })()}
                    trend={subCat?.Trend || subCat?.trend || (memory.value === "Long Term"
                      ? (dashboard.memories.longTerm?.Trend || dashboard.memories.longTerm?.trend)
                      : (dashboard.memories.shortTerm?.Trend || dashboard.memories.shortTerm?.trend))}
                    insight={getChartInsight(subCat?.name || memory.value)}
                    report={report}
                  />
                </Grid>
              </Grid>
            </>
          )}

          {/* â”€â”€ ATTENTION â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
          {cat?.name?.toLowerCase() === "attention" && (() => {
            // Prefer SubCategories from new CategoryMappings-based summary
            const summaryScores = dashboard?.activitySummary?.categories || [];
            const attSummary = summaryScores.find(
              (s) => (s.Category || s.category || "").toLowerCase() === "attention"
            );
            const newSubCats = (attSummary?.SubCategories || attSummary?.subCategories || []).map((s) => ({
              name:  s.Name  || s.name  || "",
              score: s.Score ?? s.score ?? 0,
              Chart: (s.Chart || s.chart || []).map((p) => ({ date: p.Date || p.date, score: p.Score ?? p.score ?? 0 })),
            }));
            const donutData = newSubCats.length > 0
              ? newSubCats
              : (cat?.subCategoryList || cat?.SubCategoryList || []);
            const activeChart = subCat?.Chart || subCat?.chart ||
              newSubCats.find((s) => (s.name||"").toLowerCase() === (subCat?.name||"").toLowerCase())?.Chart ||
              cat?.Chart || cat?.GameScoreList || [];
            return (
              <Grid container spacing={3} mt={1}>
                <Grid item xs={12} md={4}>
                  <DonutChart
                    title="Attention Overview"
                    chart={donutData}
                    handleSubCat={handleSubCat}
                    report={report}
                  />
                </Grid>
                <Grid item xs={12} md={8}>
                  <LineChart
                    title={subCat?.name || subCat?.Name || "Attention"}
                    color="primary"
                    chart={activeChart}
                    trend={subCat?.Trend || subCat?.trend || cat?.Trend || cat?.trend}
                    insight={getChartInsight(subCat?.name || subCat?.Name || "attention")}
                    report={report}
                  />
                </Grid>
              </Grid>
            );
          })()}

          {/* â”€â”€ ALL OTHER CATEGORIES â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
          {cat?.name?.toLowerCase() !== "memory" && cat?.name?.toLowerCase() !== "attention" && (() => {
            const summaryScores = dashboard?.activitySummary?.categories || [];
            const catSummary = summaryScores.find(
              (s) => (s.Category || s.category || "").toLowerCase() === cat?.name?.toLowerCase()
            );
            const summarySubCats = (catSummary?.SubCategories || catSummary?.subCategories || []).map((s) => ({
              name: s.Name || s.name || "",
              score: s.Score ?? s.score ?? 0,
              Chart: (s.Chart || s.chart || []).map((p) => ({ date: p.Date || p.date, score: p.Score ?? p.score ?? 0 })),
            }));
            const sl = summarySubCats.length > 0 ? summarySubCats : (cat?.subCategoryList || cat?.SubCategoryList || []);
            const activeChart = subCat
              ? (summarySubCats.find((s) => s.name?.toLowerCase() === (subCat?.name || "").toLowerCase())?.Chart
                  || subCat?.Chart || subCat?.GameScoreList || [])
              : (cat?.Chart || cat?.GameScoreList || []);
            return (
            <Grid container spacing={3} mt={1}>
              {sl.length > 0 ? (
                <>
                  <Grid item xs={12} md={4}>
                    <DonutChart
                      title={cat?.name}
                      chart={sl}
                      handleSubCat={handleSubCat}
                      report={report}
                    />
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <LineChart
                      title={subCat ? (subCat?.name || subCat?.Name) : cat?.name}
                      color={undefined}
                      chart={activeChart}
                      trend={subCat?.Trend || subCat?.trend || cat?.Trend || cat?.trend}
                      insight={getChartInsight((subCat?.name || subCat?.Name) || cat?.name)}
                      report={report}
                    />
                  </Grid>
                </>
              ) : (
                <Grid item xs={12}>
                  <LineChart
                    title={cat?.name}
                    color={undefined}
                    chart={cat?.Chart || cat?.GameScoreList || []}
                    trend={cat?.Trend || cat?.trend}
                    insight={getChartInsight(cat?.name)}
                    report={report}
                  />
                </Grid>
              )}
            </Grid>
            );
          })()}
        </div>
      )}
    </DashboardLayout>
  );
}

export default Dashboard;
